import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { AutomationClauseType } from '~/enums/AutomationClauseTypes';
import { AutomationContractType } from '~/enums/AutomationContractType';
import { AutomationDocsApplyTypes } from '~/enums/AutomationDocsApplyTypes';
import { AutomationFormErrorType } from '~/enums/AutomationFormErrorType';
import { AutomationStageType } from '~/enums/AutomationStageType';
import { AutomationStatusType } from '~/enums/AutomationStatusType';
import { TrainingStage } from '~/enums/TrainingStage';
import { Field } from '~/evifields/src/types/Field';
import { FieldId } from '~/evifields/src/types/FieldId';
import { Filters } from '~/evifields/src/types/Filters';
import { DropdownFieldValue } from '~/types/Field';
import { toLowercaseTrimmed } from '~/utils/strings';

import { GuidedAiSampleLabel, SampleReact, State } from './types';

const CLAUSE_PAGE_SIZE = 10;
const SAMPLE_PAGE_SIZE = 20;

const DEFAULT_APPLY_TO = [
  AutomationDocsApplyTypes.EXISTING,
  AutomationDocsApplyTypes.FUTURE,
];
const DEFAULT_CONTRACT_TYPE = AutomationContractType.AllTypes;

export const initialState: State = {
  manage: AutomationStageType.None,
  filter: {
    filters: [],
    fieldValues: {},
    clause: [],
    folderTree: [],
    fields: {},
  },
  quickAiForm: {
    id: null,
    name: '',
    content: '',
    sampleName: '',
    applyTo: DEFAULT_APPLY_TO,
    contractType: DEFAULT_CONTRACT_TYPE,
    errors: [],
    doesClauseExist: false,
  },
  clause: {
    name: '',
    typeFilter: [AutomationClauseType.QUICK_AI, AutomationClauseType.GUIDED_AI],
    statusFilter: [AutomationStatusType.ACTIVE],
    types: [],
    status: AutomationStatusType.ACTIVE,
    pagination: {
      pageIndex: 1,
      pageSize: CLAUSE_PAGE_SIZE,
    },
  },
  sample: {
    content: '',
    pagination: {
      pageIndex: 1,
      pageSize: SAMPLE_PAGE_SIZE,
    },
  },
  guidedAiForm: {
    id: null,
    name: '',
    description: '',
    uploadId: null,
    csvFile: null,
    samples: [],
    errors: null,
    invalids: null,
  },
  model: {
    id: null,
    version: '',
    allVersions: [],
    name: '',
    description: '',
    current: null,
    stage: TrainingStage.Empty,
    progress: null,
    showCreating: false,
  },
};

export default createSlice({
  name: 'automation',
  initialState,
  selectors: {
    selectAutomationModel: (state) => state.model,
    selectAutomationModelFilter: (state) => state.model.current?.filter,
    selectAutomationFilters: (state) => state.filter.filters,
    selectAutomationFieldValues: (state) => state.filter.fieldValues,
    selectAutomationFields: (state) => state.filter.fields,
    selectAutomationClauseSuggestions: (state) => state.filter.clause,
    selectAutomationFolderTree: (state) => state.filter.folderTree,
    selectAutomationManage: (state) => state.manage,
    selectAutomationQuickAiForm: (state) => state.quickAiForm,
    selectAutomationGuidedAiForm: (state) => state.guidedAiForm,
    selectAutomationClause: (state) => state.clause,
    selectAutomationClausePagination: (state) => state.clause.pagination,
    selectAutomationSamplePagination: (state) => state.sample.pagination,
  },
  reducers: {
    setAutomationFilter: (state: State, action: PayloadAction<Filters>) => {
      state.filter.filters = action.payload;
    },

    setAutomationFieldValues: (
      state: State,
      {
        payload,
      }: PayloadAction<{
        fieldId: string;
        values: DropdownFieldValue[] | string[];
      }>,
    ) => {
      const { fieldId, values } = payload;
      state.filter.fieldValues[fieldId] = values;
    },

    setAutomationFilterFolderTree: (
      state: State,
      action: PayloadAction<DropdownFieldValue[]>,
    ) => {
      state.filter.folderTree = action.payload;
    },

    setAutomationFilterClause: (
      state: State,
      action: PayloadAction<string[]>,
    ) => {
      state.filter.clause = action.payload;
    },

    setAutomationFilterFields: (
      state: State,
      action: PayloadAction<Record<FieldId, Field>>,
    ) => {
      state.filter.fields = action.payload;
    },

    setAutomationModel: (
      state: State,
      action: PayloadAction<State['model']>,
    ) => {
      state.model = action.payload;
    },

    resetAutomationFilters: (state: State) => {
      state.filter = initialState.filter;
    },

    resetAutomationStates: () => initialState,

    deleteAutomationModel: (state: State) => {
      state.model = {
        id: null,
        version: '',
        allVersions: [],
        name: '',
        description: '',
        current: null,
        stage: TrainingStage.Empty,
        progress: null,
        showCreating: true,
      };
    },

    setAutomationManageStage: (
      state: State,
      action: PayloadAction<State['manage']>,
    ) => {
      state.manage = action.payload;
    },

    setAutomationSample: (
      state: State,
      action: PayloadAction<State['sample']>,
    ) => {
      state.sample = action.payload;
    },

    setAutomationQuickAiDataForm: (
      state: State,
      action: PayloadAction<State['quickAiForm']>,
    ) => {
      let errors = action.payload.errors || [];

      if (state.quickAiForm.sampleName !== action.payload.sampleName) {
        errors = errors.filter(
          (e) => e !== AutomationFormErrorType.DupSampleName,
        );
      }

      if (state.quickAiForm.content !== action.payload.content) {
        errors = errors.filter((e) => e !== AutomationFormErrorType.DupContent);
      }

      state.quickAiForm = { ...action.payload, errors };
    },

    setAutomationGuidedAiDataForm: (
      state: State,
      action: PayloadAction<State['guidedAiForm']>,
    ) => {
      state.guidedAiForm = { ...action.payload };
    },

    setAutomationClause: (
      state: State,
      action: PayloadAction<State['clause']>,
    ) => {
      state.clause = action.payload;
    },

    resetAutomationQuickAiDataForm: (state: State) => {
      state.quickAiForm = {
        id: null,
        name: '',
        sampleName: '',
        content: '',
        applyTo: DEFAULT_APPLY_TO,
        contractType: DEFAULT_CONTRACT_TYPE,
        errors: [],
        doesClauseExist: false,
      };
    },

    resetAutomationGuidedAiDataForm: (state: State) => {
      state.guidedAiForm = {
        id: null,
        name: '',
        description: '',
        uploadId: null,
        csvFile: null,
        samples: [],
        errors: null,
        invalids: null,
      };
    },

    setAutomationSamplePagination: (
      state: State,
      action: PayloadAction<State['sample']['pagination']>,
    ) => {
      const { pageIndex, pageSize } = action.payload;

      if (state.sample.pagination.pageIndex !== pageIndex) {
        state.sample.pagination = { pageIndex, pageSize };
      }
    },
    setAutomationSamplePageSize: (
      state: State,
      action: PayloadAction<State['sample']['pagination']>,
    ) => {
      const { pageSize } = action.payload;

      if (state.sample.pagination.pageSize !== pageSize) {
        state.sample.pagination.pageSize = pageSize;
      }
    },

    setAutomationClausePagination: (
      state: State,
      action: PayloadAction<State['clause']['pagination']>,
    ) => {
      const { pageIndex, pageSize } = action.payload;

      if (state.clause.pagination.pageIndex !== pageIndex) {
        state.clause.pagination = { pageIndex, pageSize };
      }
    },

    setAutomationGuidedAiCsvUploadId: (
      state: State,
      action: PayloadAction<State['guidedAiForm']>,
    ) => {
      state.guidedAiForm.uploadId = action.payload.uploadId;
      state.guidedAiForm.csvFile = action.payload.csvFile;
      state.guidedAiForm.invalids = action.payload.invalids;
    },

    setAutomationGuidedAiSampleLabel: (
      state: State,
      action: PayloadAction<{ index: number; reacts: SampleReact[] }>,
    ) => {
      const { index, reacts } = action.payload;
      const label = toLowercaseTrimmed(
        reacts.find((r) => r.value)?.label ?? '',
      );

      state.guidedAiForm.samples[index].Label = !!label
        ? (label as GuidedAiSampleLabel)
        : null;
    },
  },
});
